import React, { useEffect, useRef, useState } from 'react';
import styles from './Comment.module.scss';
import {
  checkFileTypes,
  getFileSize,
  getFormattedTime,
  getNameInitials,
} from '../../screens/Requests/Sections/helper';
import { BigButton, Loader } from '../../common';
import { FaBold, FaItalic, FaUnderline, FaStrikethrough } from 'react-icons/fa';
import { CgAttachment } from 'react-icons/cg';
import cx from 'classnames';
import UploadedFiles from './UploadedFiles';
import icons from '../../assets';
import FAVICON from '../../assets/images/favicon.png';
import ReactTimeAgo from 'react-time-ago';
import { removeExifFromMultipleFiles } from '../../helpers/jsHelper';
import { REQUEST_SUPPORTED_FILE_FORMATS } from '../../constants/constants';

let previousStyle = {},
  inputValue = null;
function Comment({
  id,
  designV2 = false,
  comments,
  onCommentEnter,
  getReplies,
  hideReplies,
  loading,
  canComment,
  hideCommentBy = false,
  hideReplyOption = false,
  showDateInStatus = false,
  afterCommentComponent = null,
  isFeedbackChat = false,
  goToLastComment = false,
  disableComments = false,
}) {
  const [showButton, setshowButton] = useState();
  const [showReplyCommentOption, setShowReplyCommentOption] = useState({});
  const [textStyle, setTextStyle] = useState({});
  const [replyTextStyle, setReplyTextStyle] = useState({});
  const [file, setFile] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [replyButtonLoading, setReplyButtonLoading] = useState({});
  const { WrongCrossIcon } = icons;

  const textStyles = [
    { id: 1, name: 'bold', component: <FaBold />, style: { fontWeight: 'bold' } },
    { id: 2, name: 'italic', component: <FaItalic />, style: { fontStyle: 'italic' } },
    {
      id: 3,
      name: 'underline',
      component: <FaUnderline />,
      style: { textDecoration: 'underline' },
    },
    {
      id: 4,
      name: 'strikethrough',
      component: <FaStrikethrough />,
      style: { textDecoration: 'line-through' },
    },
  ];

  const inputRef = useRef();
  const fileRef = useRef([]);

  const scrollCommentsToBottom = () => {
    setTimeout(() => {
      const lastComment = document.querySelector(`.${styles.subWrapper} > div:last-child`);
      lastComment?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 200);
  };

  const handleFocus = (e) => {
    if (!showButton) setshowButton(true);
  };

  const handleButtonClick = async (e) => {
    const _comment = inputRef.current.value?.trim();
    if (!_comment && !file?.length) return;
    setCommentLoading(true);
    if (_comment || fileRef?.current?.length)
      onCommentEnter({
        _comment,
        textStyle: !Object.keys(textStyle)?.length ? inputValue || {} : textStyle,
        parentId: null,
        _files: fileRef?.current,
        isFeedbackChat,
      });
    inputRef.current.value = '';
    inputValue = null;
    previousStyle = textStyle;
    setTextStyle({});
    setFile([]);
    scrollCommentsToBottom();
  };

  const handleCommentWrapperClick = (e) => {
    e.stopPropagation();
  };

  const handleKeyDown = async (e) => {
    if (!e.target.value && !file?.length) return;
    if (e.keyCode === 13 && !e.shiftKey) {
      setCommentLoading(true);
      const _textStyle = {
        ...textStyle,
        start: e.target.selectionStart,
        end: e.target.selectionEnd,
      };

      const _comment = e.target.value?.trim();
      if (_comment || fileRef?.current?.length)
        onCommentEnter({
          _comment,
          textStyle: _textStyle,
          parentId: null,
          _files: fileRef?.current,
          isFeedbackChat,
        });

      e.target.value = '';
      inputValue = null;
      previousStyle = textStyle;
      setTextStyle({});
      setFile([]);

      scrollCommentsToBottom();
    }
  };

  const handleUploadFile = async (e) => {
    let _files = [...e.target.files];
    if (
      !checkFileTypes(
        _files,
        REQUEST_SUPPORTED_FILE_FORMATS,
        'The uploaded file format is not supported'
      )
    ) {
      return;
    }
    _files = await removeExifFromMultipleFiles(_files);
    setFile([...file, ..._files]);
  };

  const handleDeleteClick = (_file) => {
    const _files = file.filter((item) => item !== _file);
    setFile(_files);
  };

  const handleReplyFocus = (e, _id) => {
    if (showReplyCommentOption[_id]) setShowReplyCommentOption({ [_id]: true });
  };

  const onReplyClick = async (_id, skip) => {
    await getReplies(_id, skip);
    setShowReplyCommentOption({ [_id]: true });
  };

  const handleReplyKeyDown = async (e, _id) => {
    const _comment = e.target.value?.trim();
    if (!_comment) return;
    if (e.keyCode === 13 && !e.shiftKey) {
      setReplyButtonLoading({ [_id]: true });
      if (_comment)
        onCommentEnter({
          _comment,
          textStyle: replyTextStyle,
          parentId: _id,
          isFeedbackChat,
        });

      e.target.value = '';
    }
  };

  const handleReplyButtonClick = async (_id) => {
    const element = document.getElementById(`input_${_id}`);
    const _comment = element?.value?.trim();
    if (!_comment) return;
    setReplyButtonLoading({ [_id]: true });
    if (_comment)
      onCommentEnter({
        _comment,
        textStyle: replyTextStyle,
        parentId: _id,
        isFeedbackChat,
      });
    element.value = '';
  };

  const getTextStyle = (_id) => {
    return textStyles.find((_style) => _style.id === _id).style;
  };

  useEffect(() => {
    scrollCommentsToBottom();
    setshowButton(false);
  }, []);

  useEffect(() => {
    fileRef.current = file;
  }, [file]);

  useEffect(() => {
    scrollCommentsToBottom();
  }, [loading, goToLastComment]);

  useEffect(() => {
    setCommentLoading(false);
    setReplyButtonLoading({});
  }, [comments]);

  const handleChangeStyle = (e) => {
    if (!e.target.value) {
      inputValue = null;
      previousStyle = textStyle;
      setTextStyle({});
    } else {
      inputValue = e.target.value;
      if (!Object.keys(textStyle)?.length) {
        setTextStyle(previousStyle || {});
      }
    }
  };

  return (
    <div
      id={id}
      className={cx(
        designV2 ? styles.wrapperV2 : styles.wrapper,
        !canComment && styles.fullHeight
      )}>
      <div className={cx(styles.subWrapper, 'commentInnerContainer')}>
        {comments?.length ? (
          comments
            ?.filter((a) => a?.message || a?.images?.length > 0)
            ?.map((_comment) => {
              const name = `${_comment.created_by?.first_name} ${_comment.created_by?.last_name}`;
              const message =
                _comment.created_by?.is_guest_user || !_comment?.created_by
                  ? _comment.message?.replace(name.trim(), 'imagine.io team')
                  : _comment.message;

              return (
                <>
                  {!_comment.is_status ? (
                    <div
                      key={_comment.id}
                      className={cx(
                        styles.commentContainer,
                        !_comment.created_by?.is_guest_user || !_comment.created_by
                          ? styles.reverse
                          : ''
                      )}>
                      <div className={styles.usernameIcon}>
                        {_comment.created_by?.is_guest_user || !_comment?.created_by ? (
                          <img src={FAVICON} />
                        ) : (
                          getNameInitials(_comment.created_by)
                        )}
                      </div>
                      <div className={styles.commentWrapper}>
                        <div
                          className={`${styles.commentHeader} ${
                            _comment?.replies?.length || showReplyCommentOption[_comment.id]
                              ? styles.removeLeftAndRight
                              : ''
                          }`}>
                          {!hideCommentBy ? (
                            <div>
                              <span>
                                {_comment.created_by?.is_guest_user || !_comment?.created_by
                                  ? 'imagine.io team'
                                  : _comment.username}
                              </span>
                              <span className={styles.mutedText}> commented</span>
                            </div>
                          ) : null}
                          <span className={styles.mutedText}>
                            {getFormattedTime(_comment.created_at)}
                          </span>
                        </div>
                        {message ? (
                          <div className={styles.comment}>
                            <span
                              style={
                                _comment?.json_data?.id ? getTextStyle(_comment?.json_data?.id) : {}
                              }>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: message?.replace(/\r\n/g, '<br>'),
                                }}
                              />
                            </span>
                          </div>
                        ) : null}

                        {_comment?.images?.length > 0 ? (
                          <div
                            className={cx('d-grid gap-3 mb-4', styles.comment)}
                            style={{
                              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                              maxWidth:
                                _comment.images.length > 8
                                  ? '1200px'
                                  : `${_comment.images.length * 150}px`,
                            }}>
                            {_comment.images.map(
                              (_file) =>
                                _file.file && (
                                  <UploadedFiles
                                    key={_file.id}
                                    className={styles.uploadedFile}
                                    _file={_file}
                                    onDeleteClick={() => handleDeleteClick(_file.id)}
                                    variant={2}
                                    isReadOnly
                                  />
                                )
                            )}
                          </div>
                        ) : null}

                        {(_comment.created_by?.is_guest_user || !_comment?.created_by) &&
                        _comment?.show_model_to_user
                          ? afterCommentComponent(_comment)
                          : null}

                        {_comment?.replies?.length ? (
                          <div className={styles.replies}>
                            {_comment?.replies?.map((_item) =>
                              _item?.id ? (
                                <div key={_item?.id} className={styles.commentContainer}>
                                  <div className={styles.usernameIcon}>
                                    {_item.created_by?.is_guest_user || !_item.created_by ? (
                                      <img src={FAVICON} />
                                    ) : (
                                      getNameInitials(_item.created_by)
                                    )}
                                  </div>
                                  <div
                                    className={cx(
                                      designV2 ? styles.commentWrapper : styles.replyWrapper,
                                      designV2 ? 'py-1 px-2' : ''
                                    )}>
                                    <div className={styles.comment}>
                                      <span
                                        key={_item.id}
                                        dangerouslySetInnerHTML={{
                                          __html: _item.message?.replace(/\r\n/g, '<br>'),
                                        }}
                                      />
                                      <div className={styles.commentHeader}>
                                        <span className={styles.mutedText}>
                                          {getFormattedTime(_item.created_at)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null
                            )}
                          </div>
                        ) : null}

                        {(!hideReplyOption && canComment && showReplyCommentOption)[
                          _comment?.id
                        ] ? (
                          <div className={styles.inputBox} onClick={handleCommentWrapperClick}>
                            <textarea
                              id={`input_${_comment.id}`}
                              type="text"
                              placeholder="Type a message..."
                              onKeyDown={(e) => handleReplyKeyDown(e, _comment.id)}
                              onFocus={() => handleReplyFocus(_comment.id)}
                              style={{
                                ...(replyTextStyle?.[_comment.id]?.style || {}),
                                ...(disableComments ? { resize: 'none' } : {}),
                              }}
                              disabled={disableComments}
                              autoFocus></textarea>

                            <div className={styles.buttonsWrapper}>
                              <BigButton
                                className={styles.commentButton}
                                onClick={() => handleReplyButtonClick(_comment.id)}
                                isLoading={replyButtonLoading[_comment.id]}
                                disabled={disableComments || (!inputValue && file?.length === 0)}>
                                Send
                              </BigButton>
                            </div>
                          </div>
                        ) : (!hideReplyOption && _comment.reply_count) ||
                          !hideReplyOption ||
                          (_comment.reply_count && !_comment?.replies?.length) ? (
                          <div
                            className={styles.reply}
                            onClick={() =>
                              onReplyClick(_comment?.id, _comment?.replies?.length && true)
                            }>
                            {_comment.reply_count && !_comment?.replies?.length
                              ? _comment.reply_count === 1
                                ? `Reply (1)`
                                : `Replies (${_comment.reply_count})`
                              : 'Reply'}
                          </div>
                        ) : null}
                        {_comment?.replies?.length || showReplyCommentOption[_comment.id] ? (
                          <div
                            className={styles.collapse}
                            onClick={() => {
                              hideReplies(_comment.id);
                              setShowReplyCommentOption({ [_comment.id]: false });
                            }}
                            style={
                              replyButtonLoading[_comment.id]
                                ? { pointerEvents: 'none', opacity: 0.3 }
                                : {}
                            }>
                            Cancel
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : showDateInStatus ? (
                    _comment?.updated_at ? (
                      <>
                        {!_comment.created_by ? (
                          <div
                            key={`${_comment.id}_STATUS`}
                            className={cx(
                              styles.commentContainer,
                              !_comment.created_by?.is_guest_user || !_comment.created_by
                                ? styles.reverse
                                : ''
                            )}>
                            <div className={styles.statusChanged}>
                              <ReactTimeAgo date={Date.parse(_comment?.updated_at)} />
                            </div>
                          </div>
                        ) : null}

                        {message?.toLowerCase()?.includes('status changed from') ||
                        message?.toLowerCase()?.includes('request submitted by') ? null : (
                          <div
                            key={`${_comment.id}_MESSAGE`}
                            className={cx(styles.commentContainer)}>
                            <div className={styles.usernameIcon}>
                              {_comment.created_by?.is_guest_user || !_comment?.created_by ? (
                                <img src={FAVICON} />
                              ) : (
                                getNameInitials(_comment.created_by)
                              )}
                            </div>

                            <div className={styles.commentWrapper}>
                              <div className={styles.commentHeader}>
                                <span className={styles.mutedText}>
                                  {getFormattedTime(_comment.created_at)}
                                </span>
                              </div>
                              <div className={styles.comment}>
                                <span
                                  style={
                                    _comment?.json_data?.id
                                      ? getTextStyle(_comment?.json_data?.id)
                                      : {}
                                  }>
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: message?.replace(/\r\n/g, '<br>'),
                                    }}
                                  />
                                </span>
                              </div>

                              {(_comment.created_by?.is_guest_user || !_comment?.created_by) &&
                              _comment?.show_model_to_user
                                ? afterCommentComponent(_comment)
                                : null}
                            </div>
                          </div>
                        )}
                      </>
                    ) : null
                  ) : (
                    <div key={_comment.id} className={cx(styles.commentContainer)}>
                      <div className={styles.statusChanged}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: message?.replace(/\r\n/g, '<br>'),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })
        ) : loading ? (
          <Loader loading={loading} size={24} thickness={2} />
        ) : (
          <div className={styles.noDataText}>No comments added</div>
        )}
      </div>
      {canComment ? (
        <div className={styles.inputBox} onClick={handleCommentWrapperClick}>
          {disableComments && !isFeedbackChat ? (
            <p className={styles.info}>
              We’re prioritizing your model and will reopen this communication window for feedback
              once it’s ready.
            </p>
          ) : null}
          {file?.length ? (
            <div className={styles.uploadedFiles}>
              {file?.map((_file, i) => (
                <div key={i} className={styles.uploadedFile}>
                  <p>
                    {_file?.name} ({getFileSize(_file?.size)})
                  </p>

                  <div className={styles.closeIconWrapper} onClick={() => handleDeleteClick(_file)}>
                    <WrongCrossIcon width={16} height={16} />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <textarea
            type="text"
            placeholder="Type a message..."
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onChange={handleChangeStyle}
            disabled={disableComments}
            ref={inputRef}
            style={{
              ...(textStyle?.style || {}),
              ...(disableComments ? { resize: 'none' } : {}),
            }}></textarea>
          <>
            <div className={styles.buttonsWrapper}>
              <div className={cx(styles.textStyle, disableComments ? 'disabled' : '')}>
                {textStyles.map((_item) => (
                  <div
                    key={_item.id}
                    className={cx(
                      (+textStyle.id === +_item.id || +previousStyle?.id === +_item.id) &&
                        styles.selected,
                      'p-1'
                    )}
                    onClick={() => {
                      if (disableComments) return;

                      if (+textStyle?.id === +_item.id || +previousStyle?.id === +_item.id) {
                        previousStyle = {};
                        setTextStyle({});
                      } else {
                        previousStyle = _item;
                        if (inputValue) {
                          setTextStyle(_item);
                        } else {
                          setTextStyle({});
                        }
                      }
                    }}>
                    {_item.component}
                  </div>
                ))}
                <div className="p-1">
                  <label htmlFor={`inputUpload_${id}`}>
                    <CgAttachment />
                  </label>
                </div>
                <input
                  type="file"
                  id={`inputUpload_${id}`}
                  accept={REQUEST_SUPPORTED_FILE_FORMATS.join(', ')}
                  multiple
                  onChange={(e) => handleUploadFile(e)}
                  disabled={disableComments}
                />
              </div>
              <BigButton
                className={styles.commentButton}
                onClick={handleButtonClick}
                isLoading={commentLoading}
                disabled={disableComments || (!inputValue && file?.length === 0)}>
                Send
              </BigButton>
            </div>
          </>
        </div>
      ) : null}
    </div>
  );
}

export default Comment;
